import type { EntryOptions } from '../../types/trophy';

/**
 * Whether a turn is to be picked: the class is split over two or more, and the
 * organiser's agreement lets an entry book one (`booking_required` already
 * says the second). Otherwise the turns are listed for information only.
 */
export function choosesTurn(options: EntryOptions | null | undefined): boolean {
  return !!options?.booking_required && options.bookable_rounds.length >= 2;
}

/**
 * The turn picked, while it is still one of the class's turns: a turn picked
 * for a class the athlete is no longer in — the date or the gender changed —
 * is no choice at all.
 */
export function chosenTurn(options: EntryOptions | null | undefined, roundId: string | null): string | null {
  return options?.bookable_rounds.some((round) => round.id === roundId) ? roundId : null;
}

/**
 * Every one of the class's turns is full: there is nothing left to book, so
 * the entry cannot be made from this side — only the desk seats somebody past
 * a turn's limit.
 */
export function allTurnsFull(options: EntryOptions | null | undefined): boolean {
  return choosesTurn(options) && !!options?.bookable_rounds.every((round) => !!round.full);
}

/** Where an entry can be made from a round's card, for somebody already placed. */
export interface RoundEntryOffer {
  /** The card the button sits on. */
  roundId: string;
  /** The turn the entry books from there; null where nothing is booked. */
  bookRoundId: string | null;
  /** No seat left: the card shows no button. */
  full: boolean;
}

/**
 * The round cards that carry an "enter" button, for somebody whose category
 * is already known (`preview` has nothing missing). One entry per stage, so
 * one button per choice the entry really has:
 *
 * - a class split over turns it books → each turn, booking that one;
 * - a class with a single turn → that turn (it climbs it unbooked);
 * - turns the organiser's agreement does not let it book, or no turn at all →
 *   the first round the class climbs, the rest of the day following from it.
 *
 * Empty when there is no category to place them by.
 */
export function roundEntryOffers(preview: EntryOptions | null | undefined): RoundEntryOffer[] {
  if (!preview?.category) return [];

  const turns = preview.bookable_rounds;
  if (choosesTurn(preview)) {
    return turns.map((round) => ({ roundId: round.id, bookRoundId: round.id, full: !!round.full }));
  }
  if (turns.length === 1) return [{ roundId: turns[0].id, bookRoundId: null, full: !!turns[0].full }];

  const first = turns[0] ?? preview.other_rounds[0];
  return first ? [{ roundId: first.id, bookRoundId: null, full: false }] : [];
}
